/**
 * 도움말의 **안내 절** — 다음 할 일(사냥터 사다리·파티)과 잃고 나서야 알던 규칙(세계가 끝나면·PK 의 대가)
 * (R217·R219·R220·R221·R222, 레드팀 P2-1·P2-7·P2-10·P2-13·P2-16 — DESIGN 14.402).
 *
 * `panels/help.js` 가 `typeof … === 'function'` 으로 부른다 — 사망 도움말 정적 프로브(`tools/lib/readability-probe.mjs`)는 help.js 의
 * import 를 지우고 정해진 이름만 넣어 돌리므로, 이 절들은 실제 화면에서만 그려진다(`settlementHelp` 와 같은 방식).
 *
 * ⚠ **수치·이름을 손으로 적지 않는다.** 사냥터 이름·권장 레벨은 `HUNT_REGIONS`(지역 성장 정책)·`ZONE_KO`, 기존 섬 지역은 `ZONE_MIN_LEVEL`,
 * 거점은 역참 정류장(`STOPS`)과 아틀라스의 정착지·입구·통로 관계, 갱도는 `CAVE_MOUTHS`, PK 여부는 help.js 가 지도에서 잰 구역 집합,
 * 특전은 서버 `pubSelf().carry`, 속죄 비용은 💀 카드(서버 `you.atone`)에서 온다. 설정값으로 바뀌는 문턱은 숫자 대신 뜻으로 적는다.
 */
import { S, esc } from '../core/state.js';
import { ZONE_KO, zoneMinLevelFor, huntRegionsFor, CAVE_MOUTHS, zoneAt } from '../world/map.js';
import { STOPS } from '../world/teleport.js';
import { ATLAS_INSETS, ATLAS_LINKS } from '../world/field-atlas.js';
import { ACTION_KEYS, keyCap } from '../core/keymap.js';
import { lore } from './panels/common.js';
import { worldEndTable } from './world-end-rules.js';
import { carryHtml, vaultEndHtml } from './world-end-notice.js';
import { guestAccount } from './tab-gate.js';

/**
 * 사냥터의 **정비 거점** — 아틀라스 안의 정착지·역참 사냥터 입구를 먼저 찾고, 없으면 통로로 이은 가까운 사냥터 순서로 찾는다.
 * 정류장 id 와 아틀라스 끼움의 지역 관계를 읽는다. 유료 분지 둘은 걸어서 잇는 거점이 없어 null — 아래 `SPECIAL_WAY` 가 들어가는 법을 말한다.
 */
function baseOf(zone) {
  const queue = [`surface:${zone}`], seen = new Set(queue);
  const fieldsOf = (stop) => stop.kind === 'field' && stop.id.startsWith('f:') ? [`surface:${stop.id.slice(2)}`]
    : ATLAS_INSETS.find((inset) => inset.kind === 'settlement' && stop.id === `s:${inset.id}`)?.fields ?? [];
  while (queue.length) {
    const field = queue.shift(), stop = STOPS.find((s) => s.kind !== 'cave' && fieldsOf(s).includes(field));
    if (stop) return stop;
    for (const next of ATLAS_LINKS[field] ?? []) if (!seen.has(next)) { seen.add(next); queue.push(next); }
  }
  return null;
}

/**
 * **걸어서 들어가는 문이 없는 두 분지**다. 들어가는 자리가 코드의 특정 창이라 이름을 여기 적는다:
 *   · escape — 역참 창 아래 「검은가시 분지 입장」(ui/escape-field.js `escapeEntryCard`, 입장료)
 *   · hell   — 검은가시 분지 보급소의 지옥문지기(ui/escape-field.js `hellEntryCard`)
 * 분지가 늘거나 들어가는 자리가 바뀌면 이 표도 같이 고친다(없는 열쇠면 「—」로 남아 눈에 띈다).
 */
const SPECIAL_WAY = { escape: '역참에서 입장료를 내고', hell: '검은가시 분지 보급소의 지옥문지기' };

/**
 * R219(P2-7) — **다음 사냥터를 고르는 표.** 지도가 없고 도감에 서식지가 없어 「Lv18 이면 어디로?」를 구역에 걸어 들어가 제목 카드로
 * 배웠다. 거점별로 한 줄 — 그 거점에서 멀어질수록 강해지는 순서(권장 레벨 순)다. 섬은 마을 둘레 동심원이라 이 순서가 곧 가는 길이다.
 * @param {{pk: string[], safe: string[]}} pkZones help.js 가 지도에서 잰 PK 구역 집합
 */
export function huntLadderHelp(pkZones = { pk: [], safe: [] }) {
  const pk = new Set(pkZones.pk || []);
  // UNI-Q294 — 그 세계의 지역 판(4세계부터 분지·지옥 권장 레벨이 다르다)
  const regions = huntRegionsFor(S.world?.n);
  const zones = Object.entries(zoneMinLevelFor(S.world?.n)).filter(([z]) => z !== 'dungeon' && ZONE_KO[z])
    .map(([z, min]) => [z, regions[z]?.level ?? min]).sort((a, b) => a[1] - b[1]);
  const groups = new Map();
  for (const [z, lv] of zones) {
    const base = baseOf(z);
    const key = base ? base.ko : (SPECIAL_WAY[z] ?? '—');
    if (!groups.has(key)) groups.set(key, { key, fromStop: !!base, rows: [] });
    groups.get(key).rows.push({ z, lv });
  }
  const zoneText = ({ z, lv }) => `<b>${esc(ZONE_KO[z])}</b> Lv${lv}${pk.has(z) ? ' <span class="w">PK</span>' : ''}`;
  const lines = [...groups.values()].map((g) =>
    `<div class="kv"><span>${esc(g.key)}</span><b>${g.rows.map(zoneText).join(' → ')}</b></div>`).join('');
  const caves = CAVE_MOUTHS.map((m) => `${esc(m.name)} Lv${m.lvMin}~${m.lvMax}(${esc(ZONE_KO[zoneAt(m.x, m.y)] || '—')})`).join(' · ');
  return lore('사냥터 사다리 · 권장 레벨과 가는 길', lines
    + `<p>왼쪽은 정비할 거점, 오른쪽은 권장 레벨 순으로 정렬한 사냥터다. 권장 레벨을 갖추고 장비·보급을 준비한 뒤 사냥한다. `
    + '전체 지도에서 권장 레벨과 가는 길을 확인한다. 성문 표지판과 구역 진입 안내에는 만날 수 있는 몬스터의 최저 레벨이 표시된다.</p>'
    + `<p><b>갱도</b>(아래층, 입구의 층마다 강해진다) · ${caves}</p>`
    + '<p>마을의 <b>역참</b>(텔레포터)에서 은화를 내면 거점·갱도 입구로 바로 간다. <span class="w">PK</span> 표시는 다른 사람에게 공격받을 수 있는 사냥터다.</p>');
}

/**
 * R220(P2-10) — 처음 온 계정은 Lv10 전까지 메뉴가 짧아(R159 사용자 결정 A) 친구 둘이 같이 시작해도 누구도 파티를 못 만들었다.
 * Q276 부터 파티는 메뉴 › 사람 › 파티 한 곳이고 문턱 전에도 보인다(tab-gate.js EARLY_MENU). 도움말에서도 여는 길을 준다.
 * 수치는 서버가 `you.party` 로 준 값(최대 인원·인원당 보너스)을 쓴다.
 */
export function partyHelp() {
  const party = S.you?.party;
  if (party && party.on === false) return '<div class="kv"><span>⑦ 파티</span><b>지금은 파티 기능이 꺼져 있다</b></div>';
  const size = Number(party?.max) > 0 ? `최대 ${party.max}명이` : '파티원끼리';
  const bonus = Number(party?.bonusPct) > 0 ? `한 명 늘 때마다 총 경험치가 +${party.bonusPct}%` : '인원이 늘수록 총 경험치가 는다';
  // R227·R220 → Q276 — 파티 창은 메뉴 › 사람 › 파티, 마을에서 사람을 누르면 이름표 카드에 [파티 초대]가 있다
  return '<div class="kv"><span>⑦ 파티</span><b>친구와 같이 사냥한다</b></div>'
    + `<div class="muted">${size} 가까이서 잡은 경험치를 똑같이 나누고 ${bonus}. 보이는 사람이나 이름으로 초대한다. `
    + '파티 창은 <b>메뉴 › 사람 › 파티</b>에 있고, 마을에서 사람을 누르면 이름표 카드의 <b>[파티 초대]</b>로도 초대한다. 파티에게는 채팅을 <b>/p</b> 로 시작해 말한다.'
    + '<div class="row"><button onclick="act.openPanel(\'party\')">파티 창 열기</button></div></div>';
}

/**
 * R221(P2-13) — **PK·원주민 처치의 대가를 한 절에.** 결과(💀·경비병·원주민의 공격·선3 보호 상실·현상금)는 여러 서버 파일에
 * 흩어져 있고 화면에는 「💀 범죄레벨이 올랐다」 한 줄뿐이었다. 판정의 정본: 범죄 단계 guard.ts(`raiseCrime` — 경비병·원주민 **살해**만,
 * 사람 PK 는 안 올린다 · 상한 5 · 5단계 처형), 원주민 판정 npc-protect.ts(단계 수·적대·학살·반격·해골 표식 추격 — 전부 콘솔 설정값),
 * 성향 alignment(서버 `you.alignment.rules`), 현상금·면죄부 bounty.ts. 설정값으로 바뀌는 문턱은 뜻으로만 적는다.
 * 14.509 — 원주민이 노리는 사람(npc-protect.ts `huntMinLevel`·`huntMinRetaliate`, src/game/npc-hunt.ts 한 줄)은 기본값만 함께 적는다
 *   (「기준 레벨(기본 100)」). 기준 미만은 어떤 경우에도, 기준 이상이라도 악행 이력(성향 악1~3 · 💀 · 이번 세계 PK 처치)이 없으면
 *   원주민은 덤비지도 쫓지도 되받아치지도 않는다(되받아치기는 운영 설정 — 기본 끔). 14.507 의 「맞은 그 원주민만 되받아친다」는 낡았다.
 * 첫 공격 전 확인 창은 여기서 만들지 않았다 — R227(DESIGN 14.417)은 강공 몸짓을 공격 하나로 좁히고 안전한 땅에서 끄는 것까지다.
 */
export function pkCostHelp() {
  const rules = S.you?.alignment?.rules || S.you?.alignment;
  const pkAlign = rules?.playerKillPenalty === false ? '' : '성향이 <b>악1</b> 이하로 떨어져 <b>선3의 장비 보호</b>가 사라진다. ';
  const force = keyCap(ACTION_KEYS.force);
  return lore('PK·원주민을 해치면 · 범죄 단계와 대가', `
    <p><b>사람을 쓰러뜨리면</b> · ${pkAlign}쓰러뜨린 직후 잠시 <b>핏자국</b>이 남아 경비병이 선 마을 가장자리에서 공격받는다.
      다른 사람이 내게 <b>현상금</b>을 걸 수 있다 — 걸리면 수배 표식이 붙고 경비병과 현상금을 노리는 사람의 표적이 된다
      (원주민은 레벨이 기준 이상이고 악행 이력이 있는 수배자만 쫓는다).
      <b>💀 범죄 단계는 오르지 않는다.</b></p>
    <p><b>원주민이 노리는 사람</b> · 레벨이 기준(기본 100) 이상이고 <b>악행 이력</b>(성향 악1~3 · 💀 · 이번 세계 PK 처치)이 있는 사람뿐이다.
      레벨이 기준보다 낮으면 악행 이력이 있어도, 먼저 쳐도 원주민은 덤비지도 쫓지도 되받아치지도 않는다.
      착한 사람(악행 이력 없음)에게도 덤비지 않고, 먼저 쳐도 되받아치지 않는다(기본 설정).</p>
    <p><b>원주민·경비병을 쓰러뜨리면</b> · <b>💀 범죄 단계</b>가 오른다(원주민은 한 명에 여러 단계가 오를 수 있다).
      💀 가 쌓이면 경비병이 마을 밖에서 보는 대로 쫓아오고, 단계가 높을수록 멀리·끝까지 쫓는다.
      <b>5단계</b>에서는 경비병이 기절시켜 처형한다 — 걸려 있던 현상금은 사라지고 💀 는 0 이 된다.
      💀 가 높고 레벨이 기준 이상인 사람에게는 원주민도(자기가 확실히 강할 때) 먼저 덤빈다 — 💀 는 그 자체로 악행 이력이다.
      원주민을 쓰러뜨리면 원주민 사회가 한동안 적대하고(원주민회 가입·교환·매입소), 짧은 시간에 여럿을 쓰러뜨리면 원주민이 피하고 떨구는 것이 줄어든다. 성향도 떨어진다.</p>
    <p><b>원주민을 때리기만 해도</b> · 레벨이 기준 이상이고 악행 이력이 있으면 그 원주민이 반격하고 근처 원주민이 돕는다.</p>
    <p><b>💀 를 지우는 법</b> · 접속해 있으면 시간이 지나며 한 단계씩 내려간다 ·
      마을 신전에서 <b>속죄</b>하면 0 이 된다(비용은 💀 카드에 나온다) · 마을에서 <b>기부</b>로 한 단계씩 낮춘다(받을 때는 💀 카드에 기부 버튼이 뜬다) ·
      1단계는 경비병과 <b>흥정</b>해 뇌물로 지울 수 있다.
      <b>면죄부</b>를 지니면 — 액면이 내게 걸린 현상금보다 크면 — 수배 효과가 잠잠해진다(경비병에게서 드물게 나오고 길드 명예로 산다 · 죽으면 떨굴 수 있다).</p>
    <p><b>강공</b>(${force})을 켜 두면 원주민·사람도 누르는 대로 공격된다. 좌상단 💀 카드에 지금 단계와 속죄·기부 비용이 나온다.</p>`);
}

/**
 * R217·R218(P2-1·P2-5) — 「세계가 끝나면」 표를 사망 절 바로 아래로 올리고, 그 위에 **지금 이 세계의** 두 줄을 얹는다:
 * 창고 이월 설정(남은 시간)과 새 창고에 들어갈 특전(서버 `carry`). 전에는 원정 절 맨 뒤에만 있었다.
 */
export function worldEndHelp() {
  const carry = carryHtml(S.you);
  return lore('세계가 끝나면 · 남는 것과 사라지는 것',
    `<p>${vaultEndHtml()}</p>${carry ? `<p>${carry}</p>` : ''}${worldEndTable(esc, S.you?.carry)}`);
}

/** R222(P2-16) — 손님 계정이면 도움말 「계정」 칸에 한 줄. 연결할 수단이 없거나 이미 연결했으면 빈 문자열 */
export function guestHelpLine() {
  return guestAccount()
    ? '<div class="muted"><b class="w">이 계정은 로그인 없이 이 브라우저에만 저장돼 있다.</b> 브라우저 데이터를 지우거나 기기를 바꾸면 캐릭터·도감·집을 다시 찾기 어렵다 — <b>메뉴 › 시스템 › 계정</b>에서 Google·네이버 계정을 연결해 달라.</div>'
    : '';
}
